import { useState } from 'react';

/**
 * ProjectCard - Displays a project with title, description, and session count
 *
 * Props:
 * - project: { project_id, title, description, session_count, created_at, last_accessed }
 * - onClick: () => void - called when card is clicked
 * - onDelete: () => void - called when delete is triggered
 * - onRename: (title, description) => void - called when rename is triggered
 * - dimmed: boolean - visual dimming for unselected cards in select mode
 */
const ProjectCard = ({ project, onClick, onDelete, onRename, dimmed = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(project.title);
  const [editDescription, setEditDescription] = useState(project.description || '');
  const [showMenu, setShowMenu] = useState(false);

  const handleSaveEdit = () => {
    if (editTitle.trim() && (editTitle !== project.title || editDescription !== (project.description || ''))) {
      onRename(editTitle.trim(), editDescription.trim());
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditTitle(project.title);
    setEditDescription(project.description || '');
    setIsEditing(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
      });
    } catch {
      return '';
    }
  };

  if (isEditing) {
    return (
      <div
        className="rounded-xl p-4 transition-all"
        style={{
          backgroundColor: 'var(--bg-secondary, rgba(255,255,255,0.1))',
          border: '1px solid var(--accent-color, #3b82f6)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="text"
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          placeholder="Project title"
          className="w-full px-2 py-1.5 rounded-lg text-sm mb-2"
          style={{
            backgroundColor: 'var(--glass-input-bg)',
            color: 'var(--text-primary)',
            border: '1px solid transparent',
            outline: 'none'
          }}
          autoFocus
          maxLength={100}
        />
        <textarea
          value={editDescription}
          onChange={(e) => setEditDescription(e.target.value)}
          placeholder="Description (optional)"
          rows={2}
          className="w-full px-2 py-1.5 rounded-lg text-xs resize-none mb-3"
          style={{
            backgroundColor: 'var(--glass-input-bg)',
            color: 'var(--text-secondary)',
            border: '1px solid transparent',
            outline: 'none'
          }}
          maxLength={500}
        />
        <div className="flex gap-2">
          <button
            onClick={handleCancelEdit}
            className="flex-1 py-1.5 px-2 rounded-md text-xs"
            style={{
              backgroundColor: 'var(--glass-input-bg)',
              color: 'var(--text-primary)'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveEdit}
            disabled={!editTitle.trim()}
            className="flex-1 py-1.5 px-2 rounded-md text-xs text-white"
            style={{
              backgroundColor: editTitle.trim() ? 'var(--accent-color, #3b82f6)' : '#6b7280',
              opacity: editTitle.trim() ? 1 : 0.6
            }}
          >
            Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="project-card rounded-xl p-4 cursor-pointer transition-all"
      style={{
        backgroundColor: 'var(--bg-secondary, rgba(255,255,255,0.05))',
        border: '1px solid var(--border-color, rgba(255,255,255,0.1))',
        opacity: dimmed ? 0.5 : 1
      }}
      onClick={onClick}
    >
      {/* Header with title and actions */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* Folder icon */}
          <svg
            className="w-5 h-5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            style={{ color: 'var(--accent-color, #3b82f6)' }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          <h3
            className="font-medium text-sm truncate"
            style={{ color: 'var(--text-primary)' }}
          >
            {project.title}
          </h3>
        </div>

        {/* Kebab menu - desktop hover, mobile always visible, hidden in select mode */}
        {(onDelete || onRename) && (
        <div className="relative ml-2 flex-shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            className="project-card-menu-btn p-1.5 rounded-md transition-all"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--text-secondary)'
            }}
            title="Options"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="5" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="12" cy="19" r="2" />
            </svg>
          </button>

          {/* Dropdown menu */}
          {showMenu && (
            <>
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 40
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(false);
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '100%',
                  marginTop: '4px',
                  backgroundColor: 'var(--bg-primary, #1a1a1a)',
                  border: '1px solid var(--border-color, rgba(255,255,255,0.1))',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  zIndex: 50,
                  minWidth: '120px',
                  overflow: 'hidden'
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                    setIsEditing(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--text-primary)',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    textAlign: 'left'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary, rgba(255,255,255,0.1))'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Rename
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                    onDelete();
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#ef4444',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    textAlign: 'left'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.15)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
        )}
      </div>

      {/* Description - fixed height for consistent card size */}
      <p
        className="text-xs mb-3 line-clamp-2"
        style={{
          color: 'var(--text-secondary)',
          minHeight: '2.5rem',
          ...(project.description && project.description.length > 60 ? {
            maskImage: 'linear-gradient(to bottom, black 40%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 40%, transparent 100%)'
          } : {})
        }}
      >
        {project.description || '\u00A0'}
      </p>

      {/* Footer with session count and date */}
      <div className="flex items-center justify-between text-xs" style={{ color: 'var(--text-secondary)' }}>
        <div className="flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
          <span>{project.session_count || 0} chats</span>
        </div>
        {project.last_accessed && (
          <span>{formatDate(project.last_accessed)}</span>
        )}
      </div>
    </div>
  );
};

export default ProjectCard;
